import type { FC } from 'react';
import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import type { PermissionGroup } from 'src/constants/modulePermission';
import { SECTION } from 'src/constants/section';
import type { ModulePermission } from 'src/types/modulePermission';
import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import { useGlobalDashboards } from 'src/components/common/GlobalModules/useGlobalDashboards';

export interface SectionContextValue {
  moduleId: number;
  modulePermission?: ModulePermission;
  orgUnits?: readonly ModuleOrPlanActionOrgUnit[];
  permissionGroup?: PermissionGroup;
  section: number;
  sectionIsLoaded: boolean;
}

export const initialContextValue = {
  moduleId: SECTION.PUBLIC,
  modulePermission: undefined,
  orgUnits: undefined,
  permissionGroup: undefined,
  section: SECTION.PUBLIC,
  sectionIsLoaded: false,
};

export const SectionContext =
  createContext<SectionContextValue>(initialContextValue);

export const useSection = () => useContext(SectionContext);

interface SectionProviderProps {
  children: React.ReactNode;
  section: SECTION;
}

export const SectionProvider: FC<SectionProviderProps> = ({
  children,
  section,
}) => {
  const { user } = useUserContext();
  const { activeUserPermissionsInCurrentSurvey } = useUserPermissions();
  const params = useParams();
  const { data: globalDashboards, isFetched: sectionIsLoaded } =
    useGlobalDashboards(!!user);

  const { modulePermission, moduleId } = useMemo(() => {
    const { slug }: { slug?: string } = params;

    const getPermission = () => {
      if (section === SECTION.PUBLIC || section === SECTION.USER_ADMIN)
        return undefined;

      if (section === SECTION.DASHBOARD && user) {
        if (!slug) return undefined;

        return activeUserPermissionsInCurrentSurvey.find(
          (permission) => permission.module.slug === slug,
        );
      }

      if (user)
        return activeUserPermissionsInCurrentSurvey.find(
          (permission) => permission.module.module_type === section,
        );
    };
    const modulePermission = getPermission();

    const getModuleId = () => {
      if (modulePermission) return modulePermission.module.id;
      if (section === SECTION.DASHBOARD) {
        const globalDashboard = globalDashboards?.find(
          (dashboard) => dashboard.slug === slug,
        );
        if (globalDashboard) return globalDashboard.id;
      }
      if (
        [SECTION.SURVEY, SECTION.MY_FOLLOW_UP, SECTION.USER_ADMIN].includes(
          section,
        )
      )
        return section;

      return SECTION.PUBLIC;
    };
    const moduleId = getModuleId();

    return {
      moduleId,
      modulePermission,
      section: section || SECTION.PUBLIC,
    };
  }, [section, user, globalDashboards, activeUserPermissionsInCurrentSurvey]);

  return (
    <SectionContext.Provider
      value={{
        moduleId,
        modulePermission,
        orgUnits: modulePermission?.org_units,
        permissionGroup: modulePermission?.permission_group,
        section,
        sectionIsLoaded,
      }}
    >
      {children}
    </SectionContext.Provider>
  );
};

export default SectionProvider;
